import {
  DB_NAME,
  DB_VERSION,
  LEGACY_STORES,
  STORE_NAMES,
  STORE_SCHEMAS,
  type StoreName,
} from '@/lib/db/schema';

/**
 * IndexedDB, promisified.
 *
 * Raw rather than via `idb`: the portal uses five operations, and wrapping five
 * operations is smaller than the library that wraps all of them. Everything below is
 * the boring half — the schema next door is the part that matters.
 */

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'));
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function getDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      const existing = new Set(Array.from(db.objectStoreNames));

      for (const legacy of LEGACY_STORES) {
        if (existing.has(legacy)) db.deleteObjectStore(legacy);
      }

      for (const name of STORE_NAMES) {
        if (existing.has(name)) continue;
        const schema = STORE_SCHEMAS[name];
        const store = db.createObjectStore(name, { keyPath: schema.keyPath });
        for (const index of schema.indexes) {
          store.createIndex(index.name, index.keyPath, { unique: false });
        }
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    // Another tab is holding the old version open. Failing loudly beats hanging: the
    // catalogs are a cache, and every caller below already falls back to the network.
    request.onblocked = () => reject(new Error('the catalog database is open in another tab'));
  }).catch((error: unknown) => {
    // Do not memoise a failure — private mode denies IndexedDB entirely, and a user
    // who grants it later should not need a reload.
    dbPromise = null;
    throw error;
  });
  return dbPromise;
}

export async function dbCount(store: StoreName): Promise<number> {
  const db = await getDb();
  return promisify(db.transaction(store, 'readonly').objectStore(store).count());
}

export async function dbGetAll<T>(store: StoreName): Promise<T[]> {
  const db = await getDb();
  return promisify(db.transaction(store, 'readonly').objectStore(store).getAll()) as Promise<T[]>;
}

export async function dbGet<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  const db = await getDb();
  return promisify(db.transaction(store, 'readonly').objectStore(store).get(key)) as Promise<
    T | undefined
  >;
}

/**
 * Replace a store's contents.
 *
 * Clear and write in **one** transaction. Two transactions would leave a window where
 * the catalog is empty, and a page that reads it there renders "no songs" instead of
 * waiting — the kind of bug that only shows up on a slow machine.
 */
export async function dbReplaceAll(store: StoreName, items: readonly unknown[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(store, 'readwrite');
  const objectStore = tx.objectStore(store);
  objectStore.clear();
  for (const item of items) objectStore.put(item);
  await finished(tx);
}

export async function dbClear(store: StoreName): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).clear();
  await finished(tx);
}
